"use client";

import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Instagram, Mail, MessageCircle, Twitter, X } from "lucide-react";
import { createPortal } from "react-dom";

export type CurvedMenuItem = {
  heading: string;
  href: string;
  subheading?: string;
};

const MENU_SLIDE = {
  initial: { x: "calc(100% + 100px)" },
  enter: { x: "0", transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  exit: {
    x: "calc(100% + 100px)",
    transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
  },
};

function Curve({ height }: { height: number }) {
  const initialPath = `M100 0 L200 0 L200 ${height} L100 ${height} Q-100 ${height / 2} 100 0`;
  const targetPath = `M100 0 L200 0 L200 ${height} L100 ${height} Q100 ${height / 2} 100 0`;

  const curve = {
    initial: { d: initialPath },
    enter: { d: targetPath, transition: { duration: 1, ease: [0.76, 0, 0.24, 1] } },
    exit: { d: initialPath, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  };

  return (
    <svg className="absolute top-0 -left-[99px] h-full w-[100px] stroke-none" style={{ fill: "#ffffff" }} aria-hidden="true">
      <motion.path variants={curve} initial="initial" animate="enter" exit="exit" />
    </svg>
  );
}

function MenuFooter() {
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-4 px-10 py-5 text-sm text-black md:px-24">
      <a
        href="https://www.instagram.com/thinkincode.co.za/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
        title="Instagram"
      >
        <Instagram size={22} />
      </a>
      <a
        href="https://www.facebook.com/profile.php?id=61555149453443"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Facebook"
        title="Facebook"
      >
        <Facebook size={22} />
      </a>
      <a
        href="https://x.com/thinkincode_ZA"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="X (Twitter)"
        title="X"
      >
        <X size={22} />
      </a>
      <a
        href="https://api.whatsapp.com/send/?phone=27665733031&text&type=phone_number&app_absent=0"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        title="WhatsApp"
      >
        <MessageCircle size={22} />
      </a>
      <a
        href="https://www.tiktok.com/@thinkincode?_r=1&_t=ZS-95YthLh3znA"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="TikTok"
        title="TikTok"
        className="inline-flex items-center justify-center"
      >
        {/* TikTok icon (lucide doesn't include an official mark) */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.7 7.6c-1.1-.7-1.9-1.8-2.1-3.2V3h-3.2v12.2a2.7 2.7 0 1 1-2.2-2.7c.3 0 .5 0 .8.1V9.4a6 6 0 1 0 6 6V9.1c1.1.8 2.5 1.3 4 1.3V7.2c-1.2 0-2.4-.3-3.3-.9Z" />
        </svg>
      </a>
      <a href="mailto:hello@thinkincode.co.za" aria-label="Email" title="Email">
        <Mail size={22} />
      </a>
    </div>
  );
}

function NavLinkRow({
  item,
  index,
  onNavigate,
}: {
  item: CurvedMenuItem;
  index: number;
  onNavigate: () => void;
}) {
  return (
    <motion.div
      initial="initial"
      whileHover="whileHover"
      className="group relative flex items-center justify-between border-b border-black/20 py-4 uppercase md:py-8"
    >
      <a href={item.href} onClick={onNavigate} className="w-full">
        <div className="relative flex items-start">
          <span className="mr-2 text-3xl font-thin text-black md:text-4xl">{index}.</span>
          <div className="flex flex-col gap-1">
            <motion.span
              variants={{ initial: { x: 0 }, whileHover: { x: -16 } }}
              transition={{ type: "spring", staggerChildren: 0.075, delayChildren: 0.15 }}
              className="relative z-10 block text-3xl font-extralight text-black md:text-4xl"
            >
              {item.heading.split("").map((letter, i) => (
                <motion.span
                  key={i}
                  variants={{ initial: { x: 0 }, whileHover: { x: 16 } }}
                  transition={{ type: "spring" }}
                  className="inline-block"
                >
                  {letter}
                </motion.span>
              ))}
            </motion.span>
            {item.subheading ? <span className="text-sm font-normal text-black/60 normal-case">{item.subheading}</span> : null}
          </div>
        </div>
      </a>
    </motion.div>
  );
}

export function CurvedMenu({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: CurvedMenuItem[];
}) {
  const [height, setHeight] = useState(800);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    const update = () => setHeight(window.innerHeight || 800);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    const debugOn =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("navdebug");
    if (debugOn) {
      // eslint-disable-next-line no-console
      console.log("[CurvedMenu] state", {
        open,
        mounted,
        innerWidth: typeof window !== "undefined" ? window.innerWidth : "n/a",
      });
    }
  }, [open, mounted]);

  const navItems = useMemo(() => items.slice(0, 8), [items]);

  const content = (
    <AnimatePresence mode="wait">
      {open ? (
        <>
          <motion.div
            className="fixed inset-0 z-[11990] bg-black/10 backdrop-blur-[2px] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.25 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            variants={MENU_SLIDE}
            initial="initial"
            animate="enter"
            exit="exit"
            className="fixed right-0 top-0 z-[12000] h-[100dvh] w-screen max-w-screen-sm bg-white lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/5 text-black"
              aria-label="Close menu"
            >
              <X />
            </button>

            <div className="flex h-full flex-col justify-between pt-14">
              <div className="mt-0 flex flex-col gap-3 px-8 md:px-24">
                <div className="mb-0 border-b border-black/20 text-sm uppercase text-black">
                  <p>Navigation</p>
                </div>
                <section className="bg-transparent">
                  <div className="mx-auto max-w-2xl">
                    {navItems.map((item, i) => (
                      <NavLinkRow key={item.href} item={item} index={i + 1} onNavigate={onClose} />
                    ))}
                  </div>
                </section>
              </div>
              <MenuFooter />
            </div>
            <Curve height={height} />
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}

